import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FC, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@/context/Analytics';
import { FontFamily, FontWeight } from '@/enums/index';
import { useClientWindowScroll } from '@/hooks/useClientWindowScroll';
import { BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';
import { isElementInViewport } from '@/utils/helperFunctions';

import { ScrollableRow } from '../core/ScrollableRow';

const Tabs = styled.div`
  max-width: 794px;
  margin: 0 auto 48px;
  width: 100%;
  padding: 8px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    margin: 0 auto 90px;
  }
`;

const NavigationItem = styled.button<IThemeProps>`
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  flex: 1 0 100px;
  max-height: 74px;
  padding: 5px 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  text-decoration: none;
  color: ${({ theme }) => theme.legacy.colors.defaultText};

  &[aria-current='true'] {
    color: ${({ theme }) => theme.legacy.colors.brand.primary};
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const IconContainer = styled.span`
  border-radius: 50%;
  padding: 8px;
  background-color: transparent;
  transition: background-color 0.3s ease-out;

  &[aria-current='true'] {
    background-color: rgba(91, 28, 244, 0.1);
  }
`;

const navigationItemIconCss = css`
  height: 24px;
  width: 24px;
`;

const NavigationItemLabel = styled.span<IThemeProps>`
  display: block;
  margin-top: 8px;

  font-family: ${FontFamily.SpaceGrotesk};
  font-weight: ${FontWeight.medium};
  font-size: 12px;
  color: ${({ theme }) => theme.legacy.colors.defaultText};
  text-align: center;

  &[aria-current='true'] {
    font-weight: ${FontWeight.bold};
    color: ${({ theme }) => theme.legacy.colors.brand.primary};
  }
`;

const ROTATE_INTERVAL = 5000;

interface IEventExampleTabsProps {
  activeTabIndex: number;
  tabItems: Array<any>;
  onChangeTab: (tabIndex: number) => void;
}

export const EventExampleTabs: FC<IEventExampleTabsProps> = ({ activeTabIndex, tabItems, onChangeTab }) => {
  const [isTabsVisible, setIsTabsVisible] = useState(false);
  const [isTabManuallyChanged, setIsTabManuallyChanged] = useState(false);
  const element = useRef(null);
  const clientWindowScroll = useClientWindowScroll();
  const interval = useRef(null);
  const { t } = useTranslation();
  const analytics = useAnalytics();

  useEffect(() => {
    setIsTabsVisible(isElementInViewport(element.current));
  }, [clientWindowScroll]);

  useEffect(() => {
    if (isTabsVisible && !isTabManuallyChanged) {
      interval.current = setInterval(() => {
        if (activeTabIndex < tabItems.length - 1) {
          onChangeTab(activeTabIndex + 1);
        } else {
          onChangeTab(0);
        }
      }, ROTATE_INTERVAL);
    }

    return () => clearInterval(interval.current);
  }, [isTabsVisible, activeTabIndex, isTabManuallyChanged, tabItems, onChangeTab]);

  useEffect(() => {
    if (isTabsVisible && activeTabIndex === null) {
      onChangeTab(0);
    }
  }, [isTabsVisible, activeTabIndex, onChangeTab]);

  if (interval.current && !isTabsVisible) {
    clearInterval(interval.current);
  }

  const onTabClick = (index, analyticsKey) => {
    setIsTabManuallyChanged(true);
    onChangeTab(index);
    analytics.track(`highlights_${analyticsKey}_view`);

    if (interval.current && !isTabsVisible) {
      clearInterval(interval.current);
    }
  };

  return (
    <Tabs ref={element}>
      <ScrollableRow>
        {tabItems.map((navigationItem, index) => {
          const isSelected = activeTabIndex === index;

          return (
            <NavigationItem
              type="button"
              key={navigationItem.labelKey}
              aria-current={isSelected}
              onClick={() => onTabClick(index, navigationItem.analyticsKey)}
            >
              <IconContainer aria-current={isSelected}>
                <navigationItem.Icon css={navigationItemIconCss} />
              </IconContainer>

              <NavigationItemLabel aria-current={isSelected}>
                {t(`landing.${navigationItem.labelKey}`)}
              </NavigationItemLabel>
            </NavigationItem>
          );
        })}
      </ScrollableRow>
    </Tabs>
  );
};
